<template>
    <el-dialog title="父体配置" :visible.sync="visibleSync" width="1100px" top="5vh"
        custom-class="parent-config-dialog"
        :close-on-click-modal="false" append-to-body @open="onOpen">
        <div class="config-meta">
            <span class="meta-item"><span class="meta-label">本地父体</span>{{ form['本地父体'] || '未选择' }}</span>
        </div>

        <el-form label-width="120px" size="small">
            <el-form-item label="本地父体" required>
                <el-select v-model="form['本地父体']" filterable placeholder="选择本地父体" style="width:100%"
                    @change="onParentChange">
                    <el-option v-for="p in parentOptions" :key="p.value" :label="p.text" :value="p.value" />
                </el-select>
            </el-form-item>
        </el-form>

        <el-tabs v-model="activeTab">
            <!-- 基础配置：产品操作基础项与父体级 MSKU 生成配置统一维护 -->
            <el-tab-pane label="基础配置" name="basic">
                <el-form label-width="120px" size="small">
                    <el-form-item label="产品类型">
                        <el-select v-model="form.product_type" filterable clearable placeholder="选择产品类型"
                            style="width:100%" @change="onProductTypeChange">
                            <el-option v-for="pt in productTypes" :key="pt.value" :label="pt.label" :value="pt.value" />
                        </el-select>
                        <div class="msku-hint">父体产品类型，全品名共享；切换后「父体公用字段」会按该类型加载</div>
                    </el-form-item>
                    <el-divider content-position="left">MSKU生成配置</el-divider>
                    <div class="msku-config-section">
                        <div class="msku-hint" style="margin-bottom:10px;">仅维护父体级 MSKU 基础名规则；品名可在自身基础配置中覆盖。不会影响创建、移入或移出字段。</div>
                        <el-form-item label="生成模式">
                            <el-radio-group v-model="mskuRule.use_msku_template">
                                <el-radio :label="false">按属性拼接</el-radio>
                                <el-radio :label="true">按模板生成</el-radio>
                            </el-radio-group>
                        </el-form-item>
                        <el-form-item v-if="mskuRule.use_msku_template" label="MSKU模板">
                            <el-input v-model="mskuRule.msku_template" placeholder="如：CB-KPW5" />
                        </el-form-item>
                        <template v-else>
                            <el-form-item label="系列"><el-input v-model="mskuRule.series" placeholder="如：KPW5" /></el-form-item>
                            <el-form-item label="款式"><el-input v-model="mskuRule.style" placeholder="如：Classic" /></el-form-item>
                            <el-form-item label="分隔符"><el-input v-model="mskuRule.msku_separator" style="width:120px" /></el-form-item>
                            <div class="msku-hint msku-rule-hint">属性基础名：系列 + 款式 + 品名级 MSKU颜色。</div>
                        </template>
                        <el-form-item label="国家"><el-input v-model="mskuRule.country" placeholder="如：美国" /></el-form-item>
                        <div class="msku-hint">最终格式：基础名-供应商-国家缩写-MMDD随机数序号-后缀；供应商由生成时 SKU 对应的 ERP 品名带出。</div>
                    </div>
                </el-form>
            </el-tab-pane>

            <!-- 父体公用字段：标题 + 产品类型公用字段（表单内部滚动，不撑开弹窗） -->
            <el-tab-pane label="父体公用字段" name="parent_fields">
                <el-alert title="该部分为父体公用字段模板：同一父体下所有品名共用（含标题模板），保存后会自动应用到品名配置中"
                    type="info" :closable="false" show-icon style="margin-bottom:12px;" />
                <div class="parent-fields-scroll">
                    <el-form label-width="160px" size="small">
                        <el-form-item label="创建主标题">
                            <el-input v-model="form.create_main_title" placeholder="如: CoBak 6&quot; Case" />
                        </el-form-item>
                        <el-form-item label="创建副标题">
                            <el-input v-model="form.create_sub_title" placeholder="副标题基础" />
                        </el-form-item>
                        <el-form-item label="移出标题">
                            <el-input v-model="form.move_out_title" placeholder="移出主标题模板" />
                        </el-form-item>
                        <el-form-item label="移出副标题">
                            <el-input v-model="form.move_out_title_differentiation" placeholder="移出副标题模板" />
                        </el-form-item>
                        <template v-if="parentFields.length">
                            <el-form-item v-for="f in parentFields" :key="'pf-' + f.field_name"
                                :label="f.title_zh || f.field_name">
                                <div v-if="f.field_name === 'item_package_dimensions'" class="dims-row">
                                    <el-input-number v-model="form.parent_field_values[f.field_name].length"
                                        :min="0" :precision="2" controls-position="right" size="small" style="width:110px" />
                                    <span class="dims-x">×</span>
                                    <el-input-number v-model="form.parent_field_values[f.field_name].width"
                                        :min="0" :precision="2" controls-position="right" size="small" style="width:110px" />
                                    <span class="dims-x">×</span>
                                    <el-input-number v-model="form.parent_field_values[f.field_name].height"
                                        :min="0" :precision="2" controls-position="right" size="small" style="width:110px" />
                                    <el-select v-model="form.parent_field_values[f.field_name].unit" size="small"
                                        style="width:140px" placeholder="单位">
                                        <el-option v-for="u in dimsUnitOptions(f)" :key="u.value" :label="u.label"
                                            :value="u.value" />
                                    </el-select>
                                </div>
                                <div v-else-if="f.field_name === 'item_package_weight'" class="dims-row">
                                    <el-input-number v-model="form.parent_field_values[f.field_name].value"
                                        :min="0" :precision="2" controls-position="right" size="small" style="width:160px" />
                                    <el-select v-model="form.parent_field_values[f.field_name].unit" size="small"
                                        style="width:140px" placeholder="单位">
                                        <el-option v-for="u in weightUnitOptions(f)" :key="u.value" :label="u.label" :value="u.value" />
                                    </el-select>
                                </div>
                                <el-switch v-else-if="isBoolField(f)"
                                    v-model="form.parent_field_values[f.field_name]"
                                    active-text="Yes" inactive-text="No" />
                                <el-select v-else-if="hasEnum(f)" v-model="form.parent_field_values[f.field_name]"
                                    filterable allow-create default-first-option clearable style="width:100%"
                                    :placeholder="f.field_name">
                                    <el-option v-for="opt in f.enum" :key="opt" :label="opt" :value="opt" />
                                </el-select>
                                <el-input-number v-else-if="isNumberField(f)"
                                    v-model="form.parent_field_values[f.field_name]"
                                    controls-position="right" style="width:100%" />
                                <el-input v-else v-model="form.parent_field_values[f.field_name]"
                                    :placeholder="f.field_name" />
                            </el-form-item>
                        </template>
                        <div v-else class="config-empty">请先在上方「基础配置」选择产品类型，以加载该类型公用字段</div>
                    </el-form>
                </div>
            </el-tab-pane>

        </el-tabs>

        <span slot="footer">
            <el-button @click="visibleSync = false">取消</el-button>
            <el-button type="primary" :loading="saving" @click="save">保存父体配置</el-button>
        </span>
    </el-dialog>
</template>

<script>
export default {
    name: 'ParentConfigDialog',
    props: {
        visible: { type: Boolean, default: false },
        parent: { type: String, default: '' },
        parentList: { type: Array, default: () => [] },
    },
    data() {
        return {
            saving: false,
            activeTab: 'basic',
            parentOptions: [],
            productTypes: [],
            parentFields: [],
            mskuRule: this.blankMskuRule(),
            form: {
                '本地父体': '',
                product_type: '',
                create_main_title: '', create_sub_title: '',
                move_out_title: '', move_out_title_differentiation: '',
                parent_field_values: {},
            },
        };
    },
    computed: {
        visibleSync: {
            get() { return this.visible; },
            set(v) { this.$emit('update:visible', v); },
        },
    },
    methods: {
        onOpen() {
            this.resetForm();
            this.activeTab = 'basic';
            if (!this.productTypes.length) this.fetchProductTypes();
            this.buildParentOptions();
            if (this.parent) {
                this.form['本地父体'] = this.parent;
                this.loadConfig();
            }
        },
        resetForm() {
            this.form = {
                '本地父体': this.parent || '',
                product_type: '',
                create_main_title: '', create_sub_title: '',
                move_out_title: '', move_out_title_differentiation: '',
                parent_field_values: {},
            };
            this.parentFields = [];
            this.mskuRule = this.blankMskuRule();
        },
        blankMskuRule() {
            return {
                parent: this.parent || '', use_msku_template: false, msku_template: '',
                series: '', style: '', msku_separator: '-', country: '',
            };
        },
        buildParentOptions() {
            const list = (this.parentList && this.parentList.length) ? this.parentList : [];
            this.parentOptions = list.map(p => ({
                text: p['本地父体'] || '', value: p['本地父体'] || ''
            })).filter(p => p.value);
        },
        onParentChange() {
            // 切换父体时先清空旧数据，避免上一父体的基础/公用字段串到当前父体
            const selected = this.form['本地父体'];
            this.resetForm();
            this.form['本地父体'] = selected;
            this.mskuRule.parent = selected;
            this.loadConfig();
        },
        loadConfig() {
            const parent = this.form['本地父体'];
            if (!parent) return;
            this.$axios.get('/vue/vue_product/parent-config', {
                params: { parent }
            }).then(res => {
                if (res.code === 200 && res.data) {
                    const d = res.data;
                    Object.keys(this.form).forEach(k => {
                        if (d[k] !== undefined && d[k] !== null) {
                            this.form[k] = d[k];
                        }
                    });
                    this.form.parent_field_values = d.parent_field_values || {};
                }
                this.loadParentFields();
            }).catch(() => this.loadParentFields());
            this.loadMskuRule();
        },
        loadMskuRule() {
            const parent = this.form['本地父体'];
            if (!parent) return;
            this.$axios.get('/vue/msku-generation/parent-rule', { params: { parent } }).then(res => {
                if (res.code === 200 && res.data) {
                    this.mskuRule = Object.assign(this.blankMskuRule(), res.data, { parent });
                }
            }).catch(() => { });
        },
        fetchProductTypes() {
            this.$axios.get('/vue/vue_product/product-types').then(res => {
                if (res.code === 200 && res.data) this.productTypes = res.data;
            }).catch(() => { });
        },
        onProductTypeChange() {
            this.form.parent_field_values = {};
            this.parentFields = [];
            this.loadParentFields();
        },
        // 加载该产品类型下父体级字段（config_level === 'parent'）
        loadParentFields() {
            const pt = this.form.product_type;
            if (!pt) {
                this.parentFields = [];
                return;
            }
            this.$axios.get('/vue/vue_product/operation-fields', {
                params: { product_type: pt, scene: 'create' }
            }).then(res => {
                if (res.code !== 200) return;
                const fields = (res.data || []).filter(f => (f.config_level || 'pm') === 'parent');
                this.parentFields = fields;
                const pv = this.form.parent_field_values || {};
                fields.forEach(f => {
                    if (pv[f.field_name] === undefined) {
                        this.$set(pv, f.field_name, this.defaultFieldValue(f));
                    }
                    if (f.field_name === 'item_package_dimensions') {
                        const fv = pv[f.field_name];
                        if (fv && typeof fv === 'object' && fv.unit === undefined) {
                            this.$set(fv, 'unit', 'inches');
                        }
                    }
                });
                // 重量值归一化为 {value, unit}（兼容历史存量为纯数字）
                if (pv.item_package_weight !== undefined && pv.item_package_weight !== null) {
                    this.$set(pv, 'item_package_weight', this.normalizeWeightValue(pv.item_package_weight));
                }
                this.$set(this.form, 'parent_field_values', pv);
            }).catch(() => { });
        },
        isBoolField(f) { return f.value_type === 'boolean' || f.type === 'boolean'; },
        hasEnum(f) { return Array.isArray(f.enum) && f.enum.length > 0; },
        isNumberField(f) {
            return ['integer', 'number'].includes(f.value_type) || ['integer', 'number'].includes(f.type);
        },
        defaultFieldValue(f) {
            if (f.field_name === 'item_package_dimensions') {
                const d = f.default_value;
                if (d && typeof d === 'object') {
                    return {
                        length: d.length || 1, width: d.width || 1,
                        height: d.height || 1, unit: d.unit || 'inches',
                    };
                }
                return { length: 1, width: 1, height: 1, unit: 'inches' };
            }
            if (f.field_name === 'item_package_weight') {
                return this.normalizeWeightValue(f.default_value);
            }
            if (f.field_name === 'list_price') {
                const n = Number(f.default_value);
                return isNaN(n) ? 1 : n;
            }
            if (this.isBoolField(f)) {
                const dv = f.default_value;
                if (typeof dv === 'string') return dv.toLowerCase() === 'yes' || dv.toLowerCase() === 'true';
                return !!dv;
            }
            if (f.type === 'array') {
                // array 类型字段：default_value 可能是配置的纯值（如 brand="CoBak"）或空数组模板默认
                const dv = f.default_value;
                if (Array.isArray(dv)) return dv.length ? dv : '';
                return dv || '';
            }
            if (this.isNumberField(f)) {
                const n = Number(f.default_value);
                return isNaN(n) ? 0 : n;
            }
            return f.default_value || '';
        },
        // 重量值归一化为 {value, unit}（兼容历史存量为纯数字的情况）
        normalizeWeightValue(v) {
            if (v && typeof v === 'object') {
                return { value: v.value != null ? v.value : 1, unit: v.unit || 'pounds' };
            }
            const n = Number(v);
            return { value: isNaN(n) ? 1 : n, unit: 'pounds' };
        },
        weightUnitOptions(f) {
            if (f.unit_options && f.unit_options.length) return f.unit_options;
            return [
                { value: 'pounds', label: 'Pounds' },
                { value: 'ounces', label: 'Ounces' },
                { value: 'kilograms', label: 'Kilograms' },
                { value: 'grams', label: 'Grams' },
            ];
        },
        dimsUnitOptions(f) {
            if (f.unit_options && f.unit_options.length) return f.unit_options;
            return [
                { value: 'inches', label: 'inches' },
                { value: 'cm', label: 'cm' },
                { value: 'mm', label: 'mm' },
            ];
        },
        save() {
            if (!this.form['本地父体']) return this.$message.warning('请选择本地父体');
            const rule = Object.assign({}, this.mskuRule, { parent: this.form['本地父体'] });
            const saveMskuRule = this.hasMskuRuleInput(rule);
            if (saveMskuRule && !this.validateMskuRule(rule)) return;
            this.saving = true;
            const requests = [this.$axios.post('/vue/vue_product/parent-config', this.form)];
            if (saveMskuRule) requests.push(this.$axios.post('/vue/msku-generation/parent-rule', rule));
            Promise.all(requests).then(results => {
                if (results.every(res => res.code === 200)) {
                    this.$message.success('父体配置已保存');
                    this.$emit('saved');
                    this.visibleSync = false;
                } else {
                    const failed = results.find(res => res.code !== 200) || {};
                    this.$message.error(failed.message || '保存失败');
                }
            }).catch(e => {
                this.$message.error('保存失败: ' + e);
            }).finally(() => {
                this.saving = false;
            });
        },
        hasMskuRuleInput(rule) {
            return !!(rule && (rule.country || rule.msku_template || rule.series || rule.style));
        },
        validateMskuRule(rule) {
            if (!rule.country) {
                this.$message.warning('请填写 MSKU 配置的国家');
                return false;
            }
            if (rule.use_msku_template && !rule.msku_template) {
                this.$message.warning('模板模式必须填写 MSKU模板');
                return false;
            }
            return true;
        },
    },
};
</script>

<style scoped>
.config-meta { display: flex; flex-wrap: wrap; gap: 8px 28px; font-size: 13px; color: #606266;
    background: #f5f7fa; border-radius: 4px; padding: 10px 14px; margin-bottom: 12px; }
.meta-item { display: inline-flex; align-items: center; }
.meta-label { color: #909399; margin-right: 6px; }
.config-empty { color: #909399; font-size: 13px; padding: 12px 0; }
.msku-hint { font-size: 12px; color: #909399; line-height: 1.6; }
/* 父体公用字段：表单内部滚动，不撑开整个弹窗 */
.parent-fields-scroll { max-height: 46vh; overflow-y: auto; padding-right: 6px;
    border: 1px solid #ebeef5; border-radius: 4px; padding: 8px 12px; }
.dims-row { display: flex; align-items: center; gap: 6px; }
.dims-x { color: #909399; }
</style>

<!-- 全局样式：弹窗 append-to-body 渲染在 body 下，scoped 深度选择器无法命中 -->
<style>
/* 固定弹窗高度：内容超出时在 body 区内部滚动 */
.parent-config-dialog {
    height: min(680px, 85vh) !important;
    display: flex;
    flex-direction: column;
}
.parent-config-dialog .el-dialog__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}
</style>
